import { /* camelCase, */ sortBy, startCase } from 'lodash';
import { useMemo } from 'react';
import { ObjectType } from 'api/pushes';
import {
  FieldDataType,
  FieldTypes,
} from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  FilterableField,
  SourceType,
  // filterableFieldType,
  // DateField,
  // EstimateField,
} from 'components/organisms/tasks/fields';
import { ProjectField } from 'stores/fields';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { DateFieldFilterOperationType } from '../tasks/filters/date';
// deps for the commented code
// import { fullName } from 'utils/full-name';
// import { getRandomColor } from 'utils/get-random-color';
// import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
// import { ProjectTemplateApi } from 'data/templates/projects';
// import { useProjectTemplates } from 'queries';
// import { useUsersQuery } from 'queries/members';
// import { UserStatus, UserType } from 'api/models';
// import { InferredProgressOptions } from 'api/project';
// import { FieldOptionBase } from 'api/tasks';
// import { is } from 'ramda';

export const DashboardDateFieldFilterOperationTypes = [
  DateFieldFilterOperationType.IsThisWeek,
  DateFieldFilterOperationType.IsLastWeek,
  DateFieldFilterOperationType.IsThisMonth,
  DateFieldFilterOperationType.IsLastMonth,
  DateFieldFilterOperationType.IsThisQuarter,
  DateFieldFilterOperationType.IsLastQuarter,
  DateFieldFilterOperationType.IsThisYear,
  DateFieldFilterOperationType.IsAny,
  DateFieldFilterOperationType.IsMoreThan,
  DateFieldFilterOperationType.IsExactly,
  DateFieldFilterOperationType.IsLessThan,
  DateFieldFilterOperationType.IsAfter,
  DateFieldFilterOperationType.IsOn,
  DateFieldFilterOperationType.IsBefore,
  DateFieldFilterOperationType.IsEmpty,
  DateFieldFilterOperationType.IsNotEmpty,
];

const FIELD_ID_ARR = -500;
const FIELD_ID_PROJECT_FEE = FIELD_ID_ARR + 1;
// const FIELD_ID_PROJECT_OWNER = FIELD_ID_PROJECT_FEE + 1;
// const FIELD_ID_TEMPLATE = FIELD_ID_PROJECT_OWNER + 1;
// const FIELD_ID_INFERRED_PROGRESS = FIELD_ID_TEMPLATE + 1;
// const FIELD_ID_DUE_DATE = FIELD_ID_INFERRED_PROGRESS + 1;
// const FIELD_ID_START_DATE = FIELD_ID_DUE_DATE + 1;
// const FIELD_ID_LAST_STATUS_UPDATE = FIELD_ID_START_DATE + 1;
// const FIELD_ID_CURRENT_PHASE = FIELD_ID_LAST_STATUS_UPDATE + 1;
// const FIELD_ID_ESTIMATED_DUE_DATE = FIELD_ID_CURRENT_PHASE + 1;

export const ArrField: ProjectField = {
  fieldType: FieldTypes.SingleSelect,
  fieldDataType: FieldDataType.Number,
  type: FieldType.Number,
  customField: false,
  fieldId: FIELD_ID_ARR,
  fieldName: 'arr',
  fieldLabel: 'ARR',
  optionsAvailable: false,
  private: true,
  isEnabled: true,
  objectType: ObjectType.Project,
  userCreated: false,
  metaData: {
    required: true,
    rangeAvailable: true,
    default: 0,
    range: {
      min: 0,
    },
  },
  sourceType: SourceType.Project,
};
export const ProjectFeeField: ProjectField = {
  ...ArrField,
  fieldId: FIELD_ID_PROJECT_FEE,
  fieldName: 'projectFee',
  fieldLabel: 'Project fee',
};

/**
 * Returns Lookup with all the fields.
 *
 */
export const useFilterFields = (): {
  isLoading: boolean;
  data: FilterableField[];
} => {
  const { fields: projectFields, loading: isLoading } =
    useProjectFieldsAsFilterableFields();

  const data = useMemo(
    () =>
      sortBy(
        [...projectFields],
        ({ fieldLabel, fieldName }) => fieldLabel ?? startCase(fieldName),
      ),
    [projectFields],
  );

  return useMemo(
    () => ({
      data,
      isLoading,
    }),
    [data, isLoading],
  );
};

// Code for the old useFilterFields() function. if needed, make sure to uncomment deps also

// const getInferredProgressField = (
//   options: FieldOptionBase[],
// ): ProjectField => ({
//   fieldType: FieldTypes.SingleSelect,
//   fieldDataType: FieldDataType.Text,
//   customField: false,
//   type: FieldType.SingleSelect,
//   fieldId: FIELD_ID_INFERRED_PROGRESS,
//   fieldName: 'inferredProgress',
//   optionsAvailable: true,
//   metaData: {
//     options,
//   },
//   private: true,
//   isEnabled: true,
//   objectType: ObjectType.Project,
//   userCreated: false,
//   sourceType: SourceType.Project,
// });

// const getTemplateField = (
//   templates: Pick<ProjectTemplateApi, 'templateName' | 'templateId'>[],
// ): ProjectField => ({
//   fieldType: FieldTypes.SingleSelect,
//   fieldDataType: FieldDataType.Text,
//   customField: false,
//   type: FieldType.SingleSelect,
//   fieldId: FIELD_ID_TEMPLATE,
//   fieldName: 'template',
//   optionsAvailable: true,
//   metaData: {
//     options: templates.map((x) => ({
//       label: x.templateName || '',
//       value: x.templateId as number,
//       color: getRandomColor(x.templateId as number),
//       active: true,
//     })),
//   },
//   private: true,
//   isEnabled: true,
//   objectType: ObjectType.Project,
//   sourceType: SourceType.Project,
// });

// const getProjectOwnerField = (
//   options: FieldOptionBase<number>[],
// ): ProjectField => ({
//   fieldType: FieldTypes.SingleSelect,
//   fieldDataType: FieldDataType.Text,
//   customField: false,
//   type: FieldType.SingleSelect,
//   fieldId: FIELD_ID_PROJECT_OWNER,
//   fieldName: 'projectOwner',
//   optionsAvailable: true,
//   metaData: {
//     options,
//   },
//   private: false,
//   isEnabled: true,
//   objectType: ObjectType.Project,
//   sourceType: SourceType.Project,
// });

// /* const CurrentPhaseField = (
//   options: FieldOptionBase<number>[],
// ): ProjectField => ({
//   fieldType: FieldTypes.SingleSelect,
//   fieldDataType: FieldDataType.Text,
//   customField: false,
//   type: FieldType.SingleSelect,
//   fieldId: FIELD_ID_CURRENT_PHASE,
//   fieldName: 'currentPhase',
//   optionsAvailable: true,
//   metaData: {
//     options,
//   },
//   private: false,
//   isEnabled: true,
//   objectType: ObjectType.Project,
// }); */

// const ProjectDueDateField: DateField = {
//   type: FieldType.Date,
//   customField: false,
//   fieldId: FIELD_ID_DUE_DATE,
//   fieldName: 'dueDate',
//   fieldLabel: 'Due date',
//   private: false,
//   sourceType: SourceType.Project,
// };

// const ProjectStartDateField: DateField = {
//   ...ProjectDueDateField,
//   fieldName: 'startDate',
//   fieldLabel: 'Start date',
//   fieldId: FIELD_ID_START_DATE,
//   sourceType: SourceType.Project,
// };

// const EstimatedDueDateField: EstimateField = {
//   customField: false,
//   private: false,
//   type: FieldType.Estimate,
//   fieldName: 'estimatedDueDate',
//   fieldLabel: 'Estimated Due Date',
//   fieldId: FIELD_ID_ESTIMATED_DUE_DATE,
//   sourceType: SourceType.Project,
// };

// const LastStatusUpdateField: DateField = {
//   ...ProjectDueDateField,
//   fieldLabel: 'Last project update published at',
//   fieldName: 'lastStatusUpdate',
//   fieldId: FIELD_ID_LAST_STATUS_UPDATE,
//   sourceType: SourceType.Project,
// };

// export const useFilterFields = (): {
//   isLoading: boolean;
//   data: FilterableField[];
// } => {
//   const { data: users = [], isLoading: isUsersLoading } = useUsersQuery();
//   const { data: templates = [], isLoading: isTemplatesLoading } =
//     useProjectTemplates();
//   const { data: projectCustomFields, isLoading: isProjectCustomFieldsLoading } =
//     useProjectCustomFields();
//   /* const { data: phases, isLoading: isPhasesLoading } =
//     useGetPhasesProjectTemplatesQuery(); */

//   const isLoading = useMemo(() => {
//     return (
//       isUsersLoading || isTemplatesLoading || isProjectCustomFieldsLoading /* ||
// isPhasesLoading, */
//     );
//   }, [isUsersLoading, isTemplatesLoading, isProjectCustomFieldsLoading]);

//   const data = useMemo(
//     () =>
//       sortBy(
//         [
//           ArrField,
//           ProjectFeeField,
//           getInferredProgressField(
//             InferredProgressOptions as unknown as FieldOptionBase[],
//           ),
//           ProjectDueDateField,
//           ProjectStartDateField,
//           EstimatedDueDateField,
//           LastStatusUpdateField,
//           ...projectCustomFields.map((field) => ({
//             ...field,
//             fieldName: camelCase(field.fieldName),
//             type: filterableFieldType(field),
//           })),
//           getProjectOwnerField(
//             users
//               .filter(
//                 (u) =>
//                   u.userType === UserType.Native &&
//                   u.userStatus !== UserStatus.Inactive,
//               )
//               .map((u) => ({
//                 label: fullName(u),
//                 value: u.userId,
//                 color: getRandomColor(u.userId),
//                 active: true,
//               })),
//           ),
//           /* CurrentPhaseField(
//             (phases ?? []).map(
//               ({
//                 projectPhaseId,
//                 projectPhaseName,
//                 templateId,
//                 templateName,
//               }) => ({
//                 label: `${projectPhaseName} - ${templateName}`,
//                 value:
//                   `${projectPhaseName}_____${templateId}` as unknown as number,
//                 color: getRandomColor(projectPhaseId),
//               }),
//             ),
//           ), */
//           getTemplateField(templates),
//         ].filter((x) => Boolean(x)) as FilterableField[],
//         ({ fieldLabel, fieldName }) => fieldLabel ?? startCase(fieldName),
//       ),
//     [users, templates, projectCustomFields /* , phases */],
//   );

//   return useMemo(
//     () => ({
//       isLoading,
//       data,
//     }),
//     [isLoading, data],
//   );
// };
